import { useEffect, useRef, useState } from "react";
import { buildBranchReleaseEntries } from "../homeworkReleasePlan";
import useReleaseWriter from "./useReleaseWriter";
import useFirestore from "shared/hooks/useFirestore";
import { SelectedItem, branch, folder, setting } from "shared/types/Report";

interface effectiveDate{
    start:string,
    end:string,
}

const useReleaseView = () => {
    const now = new Date();
    const parseDate = (selected_date:Date) => {
        const month = (selected_date.getMonth() + 1).toString().length === 1 ? "0" + (selected_date.getMonth() + 1).toString() : selected_date.getMonth() + 1;
        const date = selected_date.getDate().toString().length === 1 ? "0" + selected_date.getDate().toString() : selected_date.getDate();
        return `${selected_date.getFullYear()}-${month}-${date}`
    }
    const {readFirestore} = useFirestore();
    const { publish, retryPending, pendingReleaseCount } = useReleaseWriter("ReportAssignBranch");
    const releaseInFlight = useRef(false);
    const [releaseFailureMessage, setReleaseFailureMessage] = useState("");
    const [branchList, setBranchList] = useState<branch[]>([]);
    const [selectedBranchList, setSelectedBranchList] = useState<string[]>([])
    const [folderList, setFolderList] = useState<folder[]>([]);
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
    const [effectiveDate, setEffectiveDate] = useState<effectiveDate>({
        start:parseDate(now),
        end:parseDate(now)
    })
    const [setting, setSetting] = useState<setting | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isOpenWarningModal, setIsOpenWarningModal] = useState<boolean>(false);
    const [isOpenReleaseSucceedModal, setIsOpenReleaseSucceedModal] = useState<boolean>(false);
    const [isOpenReleaseFailedModal, setIsOpenReleaseFailedModal] = useState<boolean>(false);
    const [isOpenNullModal, setIsOpenNullModal] = useState<boolean>(false);
    const [isOpenDateErrorModal, setIsOpenDateErrorModal] = useState<boolean>(false);
    
    useEffect(() => {
        initializeData();
    }, [])

    const initializeData = async () => {
        setIsLoading(true);
        try {
            // 병렬로 지점 목록과 폴더 목록을 동시에 가져오기
            await Promise.all([
                getBranchList(),
                getFolderReportList()
            ]);
        } catch (error) {
            console.error('데이터 초기화 중 오류 발생:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getBranchList = async() => {
        const branch_list = await readFirestore("User/Auth/Branch").then(res => {
            const list:branch[] = [];
            const doc_list = res.docs;
            const doc_list_length = res.docs.length;
            for(let i = 0; i < doc_list_length; i++){
                const branch_id = doc_list[i].id;
                const branch_name = doc_list[i].data().name;
                list.push({id:branch_id, name:branch_name})
            }
            return list;
        })
        setBranchList(branch_list);
    }

    const onSelectBranchClickHandler = (id:string, checked:boolean) => {
        setSelectedBranchList(prev => {
            if (checked) {
                return prev.includes(id) ? prev : [...prev, id];
            } else {
                return prev.filter(selectedId => selectedId !== id);
            }
        });
    }

    const getFolderList = async (path: string = "Report"): Promise<folder[]> => {
        const folderList = await readFirestore(path).then(res => {
            const list: folder[] = [];
            res.docs.forEach((doc:any) => {
                const folder_id = doc.id;
                const folder_name = doc.data().name;
                const folder_type = doc.data().type;
                list.push({
                    id: folder_id,
                    name: folder_name,
                    type: folder_type,
                    path: path,
                    subFolders: folder_type === 'folder' ? [] : undefined,
                });
            });
            return list;
        });

        folderList.sort(function(a:folder, b:folder):number{
            if(a.name < b.name){
                return -1;
            }else{
                return 1;
            }
        })
    
        // 하위 폴더들을 병렬로 가져오기
        const folderPromises = folderList
            .filter(folder => folder.type === 'folder')
            .map(async (folder) => {
                folder.subFolders = await getFolderList(`${path}/${folder.id}/Report`);
                return folder;
            });
        
        // 병렬 처리로 모든 하위 폴더를 동시에 가져오기
        await Promise.all(folderPromises);
    
        return folderList;
    };
    
    const getFolderReportList = async () => {
        const folderList = await getFolderList();
        setFolderList(folderList);
    };

    const onSelectReportClickHandler = (folder: folder, isChecked: boolean, parentStart: string = "") => {
        let toggle = false;
        let start = parentStart || folder.path || ""; // 최상위 폴더의 경로 설정
    
        const updateSelectedFolders = (folder: folder) => {
            setSelectedItems((prev: SelectedItem[]) => {
                // 폴더가 선택된 경우 start 경로 설정
                if (!toggle && isChecked && folder.path && folder.type === "folder") {
                    toggle = true;
                    start = parentStart || folder.path || "";
                }
    
                // 보고서(report)가 선택된 경우
                if (isChecked && folder.path && folder.type === "report") {
                    toggle = false;
                    // 이미 선택된 보고서가 아닌 경우 추가
                    if (!prev.some(item => item.id === folder.id)) {
                        return [...prev, { id: folder.id, start: start, path: folder.path, name: folder.name }];
                    }
                } 
                // 보고서가 선택 해제된 경우
                else if (!isChecked && folder.type === "report") {
                    return prev.filter(item => item.id !== folder.id);
                }
                return prev;
            });
        };
    
        // 현재 폴더 또는 보고서에 대한 선택/해제 처리
        updateSelectedFolders(folder);
    
        // 하위 폴더가 있을 경우 재귀적으로 처리
        if (folder.subFolders && folder.subFolders.length > 0) {
            folder.subFolders.forEach(subFolder => {
                // 하위 폴더의 체크박스 상태 업데이트
                const checkBox = document.getElementById(subFolder.id) as HTMLInputElement;
                if (checkBox) {
                    checkBox.checked = isChecked;
                }
                // 재귀적으로 하위 폴더 및 보고서 처리, 최상위 폴더의 경로 전달
                onSelectReportClickHandler(subFolder, isChecked, start);
            });
        }
    };

    const onEffectiveDateChangeHandler = (name:string, value:string) => {
        if(name === "end" && value < effectiveDate.start){
            setIsOpenDateErrorModal(true);
            return;
        }
        setEffectiveDate({
            ...effectiveDate,
            [name]:value
        })
    }

    const onDateErrorModalConfirmClickHandler = () => {
        setIsOpenDateErrorModal(false);
    }

    const onReportReleseClickHandler = () => {
        setIsOpenWarningModal(true);
    }

    const onReportReleaseCancelClickHandelr = () => {
        setIsOpenWarningModal(false);
    }

    const onReportReleaseConfirmhandler = () => {
        reportRelese();
        setIsOpenWarningModal(false);
    }

    const reportRelese = async () => {
        if (releaseInFlight.current || isLoading) return;
        releaseInFlight.current = true;
        setIsLoading(true);
        try {
            const entries = buildBranchReleaseEntries(selectedBranchList, selectedItems, effectiveDate);
            if (await publish(entries)) setIsOpenReleaseSucceedModal(true);
        } catch (error) {
            setReleaseFailureMessage(error instanceof Error ? error.message : "과제 배포에 실패했습니다. 같은 화면에서 다시 시도해주세요.");
            setIsOpenReleaseFailedModal(true);
        } finally {
            releaseInFlight.current = false;
            setIsLoading(false);
        }
    };

    const onRetryReleaseClickHandler = async () => {
        if (releaseInFlight.current || isLoading) return;
        releaseInFlight.current = true;
        setIsLoading(true);
        setIsOpenReleaseFailedModal(false);
        try {
            if (await retryPending()) setIsOpenReleaseSucceedModal(true);
        } catch (error) {
            setReleaseFailureMessage(error instanceof Error ? error.message : "과제 배포에 실패했습니다. 다시 시도해주세요.");
            setIsOpenReleaseFailedModal(true);
        } finally {
            releaseInFlight.current = false;
            setIsLoading(false);
        }
    };

    const onNullModalConfirmClickHandler = () => {
        setIsOpenNullModal(false);
    }

    const onSucceedModalConfirmClickHandler = () => {
        setIsOpenReleaseSucceedModal(false);
    }

    const onFailedModalConfirmClickHandler = () => {
        setIsOpenReleaseFailedModal(false);
    }

    // ReleaseTable에서 사용하는 추가 핸들러들
    const onOpenSettingModalClickHandler = () => {
        // 학습 설정 모달 열기 로직
        console.log("학습 설정 모달 열기");
    }

    const onDeleteSettingClickHandler = () => {
        setSetting(undefined);
    }

    const onOpenScheduleModalClickHandler = () => {
        // 일괄일정관리 모달 열기 로직
        console.log("일괄일정관리 모달 열기");
    }

    const onOpenDateScheduleModalClickHandler = () => {
        // 날짜지정일정관리 모달 열기 로직
        console.log("날짜지정일정관리 모달 열기");
    }

    return {
        branchList, 
        selectedBranchList,
        folderList, 
        selectedItems,
        effectiveDate, 
        onEffectiveDateChangeHandler,
        onSelectReportClickHandler,
        onSelectBranchClickHandler,
        isOpenDateErrorModal, 
        onDateErrorModalConfirmClickHandler,
        onReportReleseClickHandler,
        isOpenNullModal, 
        onNullModalConfirmClickHandler,
        isOpenReleaseSucceedModal, 
        onSucceedModalConfirmClickHandler,
        isOpenReleaseFailedModal, 
        onFailedModalConfirmClickHandler,
        isOpenWarningModal, 
        onReportReleaseConfirmhandler, 
        onReportReleaseCancelClickHandelr,
        setting, 
        onOpenSettingModalClickHandler, 
        onDeleteSettingClickHandler,
        onOpenScheduleModalClickHandler, 
        onOpenDateScheduleModalClickHandler,
        isLoading, releaseFailureMessage, pendingReleaseCount, onRetryReleaseClickHandler
    }
}

export default useReleaseView;